import EmailInput from '~/components/EmailInput';
import QuestionLabel from './QuestionLabel';
import { FormPart, FormStateName, part, state } from '~/lib/FormCssApi';

import { useContext, useEffect, useState } from 'react';
import SelectItemButton from './components/SelectItemButton.jsx';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import MoneyInput from '~/components/MoneyInput';
import PrintableGiftCertificate from '~/components/giftcertificates/PrintableGiftCertificate';
import { addYears } from 'date-fns';
import { FormContext } from '~/components/forms/FormContext';
import ErrorMessage from '~/components/ErrorMessage';
import { getBrowserTimezone } from '~/lib/DateTime';
import SendOnInput from '~/components/giftcertificates/SendOnInput';
import { useEventTypesQuery, usePublicPackagesQuery } from '~/lib/SharedQueries.js';

export const DEFAULT_GIFT_CERTIFICATE_VALUES = ['25', '50', '75', '100'];

const BackgroundImages = [
    'https://images.unsplash.com/photo-1513151233558-d860c5398176?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwxfHxiaXJ0aGRheSUyMGJhY2tncm91bmR8ZW58MHwwfHx8MTc0MDQ5MjQ1MHww&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1444159759392-aeeb3d5851c1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwxMHx8d2VsbG5lc3MlMjBiYWNrZ3JvdW5kfGVufDB8MHx8fDE3NDA0OTI3Mjd8MA&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1515705576963-95cad62945b6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1504196606672-aef5c9cefc92?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwxMXx8YmlydGhkYXklMjBiYWNrZ3JvdW5kfGVufDB8MHx8fDE3NDA0OTI0NTB8MA&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1516414559093-91c1c3d7359c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwyfHxsb3ZlJTIwYmFja2dyb3VuZHxlbnwwfDB8fHwxNzQwNDkyNTU1fDA&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1519751138087-5bf79df62d5b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwxMnx8Z2lmdCUyMGNlcnRpZmljYXRlJTIwYmFja2dyb3VuZHxlbnwwfDB8fHwxNzQwNDkxMDM4fDA&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1480422080719-4d25a4c7e22c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwyNXx8aG9saWRheSUyMGJhY2tncm91bmR8ZW58MHwwfHx8MTc0MDQ5MjU4OHww&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1506102383123-c8ef1e872756?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1557517356-8b497896a6fb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.0.3&q=80',
];

const BackgroundImagePicker = ({ value, onChange, className, customImages }) => {
    // Use custom images if available, otherwise use default images
    const imagesToDisplay = (customImages && customImages.length > 0) ? customImages : BackgroundImages;

    function getSrcFromUrl(url)
    {
        // If it is unsplash then add &w=150 to the end
        if (url.includes('unsplash.com')) {
            return `${url}&w=150`;
        }

        return url;
    }

    // If there's only one option, automatically select it and don't render the picker
    useEffect(() => {
        if (imagesToDisplay.length === 1 && value !== imagesToDisplay[0]) {
            onChange(imagesToDisplay[0]);
        }
    }, [imagesToDisplay, value]); // eslint-disable-line react-hooks/exhaustive-deps

    // Don't render the picker if there's only one option
    if (imagesToDisplay.length === 1) {
        return null;
    }

    return (
        <div
            {...part(FormPart.GIFT_CERTIFICATE_IMAGES)}
            className={`flex items-center space-x-4 overflow-y-scroll px-1 py-2 ${className}`}
        >
            {imagesToDisplay.map((url, index) => (
                <img
                    key={url}
                    {...part(FormPart.GIFT_CERTIFICATE_IMAGE)}
                    {...state(value === url && FormStateName.SELECTED)}
                    src={getSrcFromUrl(url)}
                    onClick={() => onChange(value === url ? '' : url)}
                    className={`rounded-lg cursor-pointer max-w-[150px] max-h-[100px] ${value === url ? 'outline outline-2 outline-offset-2 themed-outline' : ''}`}
                />
            ))}
        </div>
    );
};

const AmountOptions = ({ settings, value, onChange, className }) => {
    const [selectedOptionId, setSelectedOptionId] = useState('');
    const [customAmount, setCustomAmount] = useState('');
    const [hasLoaded, setHasLoaded] = useState(false);

    const CUSTOM_VALUE = '__CUSTOM__';

    const { data: eventTypesData } = useEventTypesQuery();
    const { data: publicPackagesData } = usePublicPackagesQuery();
    const eventTypes = eventTypesData || [];
    const publicPackages = publicPackagesData?.packages || [];

    const linked = settings?.giftCertLinked || {};
    const enabledEventIds = linked?.event_type_ids || [];
    const enabledPackageIds = linked?.package_ids || [];
    const enabledSubscriptionIds = linked?.subscription_ids || [];

    const amountOptions = (
        settings?.possibleValues || DEFAULT_GIFT_CERTIFICATE_VALUES
    )
        .filter(item => (typeof item === 'object' ? !!item?.value : !!item))
        .map((item, idx) => {
            if (typeof item === 'object') {
                return {
                    id: `preset_${idx}`, // unique ID for selection tracking
                    amount: item?.value,
                    name: item?.name,
                    description: item?.description,
                };
            } else {
                return {
                    id: `preset_${idx}`, // unique ID for selection tracking
                    amount: item,
                    name: null,
                    description: null,
                };
            }
        });

    // Append linked Event Types
    enabledEventIds.forEach(id => {
        const et = eventTypes.find(e => parseInt(e.id) === parseInt(id));
        if (et && parseFloat(et.price) > 0) {
            amountOptions.push({
                id: `event_type_${et.id}`, // unique ID for selection tracking
                amount: et.price,
                name: et.name,
                description: null,
            });
        }
    });

    // Append linked Packages and Subscriptions
    const linkedPackageSet = new Set([...enabledPackageIds, ...enabledSubscriptionIds].map(x => parseInt(x)));
    publicPackages.forEach(p => {
        if (linkedPackageSet.has(parseInt(p.id)) && parseFloat(p.price) > 0) {
            amountOptions.push({
                id: `package_${p.id}`, // unique ID for selection tracking
                amount: p.price,
                name: p.name,
                description: p.description,
            });
        }
    });

    if (!settings?.disableCustom) {
        amountOptions.push({
            id: CUSTOM_VALUE,
            label: 'Custom',
            name: 'Custom Amount',
        });
    }

    useEffect(() => {
        if (selectedOptionId === CUSTOM_VALUE) {
            onChange(customAmount);
        } else {
            // Find the selected option and use its amount
            const selectedOption = amountOptions.find(opt => opt.id === selectedOptionId);
            if (selectedOption) {
                onChange(selectedOption.amount);
            }
        }
    }, [selectedOptionId, customAmount]);

    useEffect(() => {
        setHasLoaded(true);
    }, []);

    // Auto-select if there's only one option and Custom is disabled
    useEffect(() => {
        if (amountOptions.length === 1 && settings?.disableCustom && !selectedOptionId) {
            setSelectedOptionId(amountOptions[0].id);
        }
    }, [amountOptions, settings?.disableCustom, selectedOptionId]);

    return (
        <div className={className}>
            <QuestionLabel label="Amount" />
            <div className="@container">
            <div
                {...part(FormPart.SELECTION_LIST)}
                className="grid @xs:grid-cols-2 gap-4"
            >
                {amountOptions.map((opt, idx) => {
                    const isSelected = selectedOptionId === opt.id;
                    const isCustom = opt.id === CUSTOM_VALUE;
                    return (
                        <SelectItemButton
                            key={opt.id}
                            onClick={() => setSelectedOptionId(opt.id)}
                            isSelected={isSelected}
                            layout="grid"
                            enableQuantity={false}
                        >
                            <div className="font-semibold">
                                {opt.name ? opt.name : formatCurrency(opt.amount)}
                            </div>
                            {!!opt.description && (
                                <div className="text-gray-500 text-sm mt-1">{opt.description}</div>
                            )}
                            {opt.name && !isCustom && (
                                <div className="text-gray-500 text-sm mt-1">{formatCurrency(opt.amount)}</div>
                            )}
                            {isSelected && isCustom && (
                                <div className="mt-2">
                                    <MoneyInput
                                        {...part(FormPart.INPUT)}
                                        value={customAmount}
                                        onChange={setCustomAmount}
                                        autoFocus={!customAmount && hasLoaded}
                                    />
                                </div>
                            )}
                        </SelectItemButton>
                    );
                })}
            </div>
            </div>
        </div>
    );
};

const GiftCertificateQuestion = ({
    label,
    value: formValue,
    setValue: setFormValue,
    settings,
}) => {
    const [value, setValue] = useState({});
    const { form } = useContext(FormContext);

    // Only save off value if we have all the data
    useEffect(() => {
        if (value?.amount) {
            setFormValue({
                ...value,
                timezone: getBrowserTimezone(),
            });
        }
    }, [value]);

    const isError = !!settings?.isError;

    return (
        <div>
            <QuestionLabel
                label={label}
                settings={settings}
                className="text-center"
            />
            <div
                {...part(FormPart.GIFT_CERTIFICATE_PREVIEW)}
                className="mb-4"
            >
                <PrintableGiftCertificate
                    value={value?.amount || 0}
                    recipient={{
                        name: value?.recipientName,
                        email: value?.recipientEmail,
                    }}
                    business={form?.business}
                    message={value?.message}
                    image={value?.image}
                    code="ABCD1234"
                    expires={
                        parseFloat(settings?.expires) > 0
                            ? addYears(new Date(), settings?.expires)
                            : ''
                    }
                />
            </div>

            <BackgroundImagePicker
                value={value?.image}
                onChange={value => setValue(old => ({ ...old, image: value }))}
                className="mb-4"
                customImages={settings?.customBackgroundImages}
            />

            <div
                {...part(FormPart.CONTROL)}
                className={`mb-4 ${isError && !value?.recipientName ? 'is-error' : ''}`}
            >
                <QuestionLabel label="Recipient Name" />
                <input
                    {...part(FormPart.INPUT)}
                    type="text"
                    autoComplete="name"
                    value={value?.recipientName || ''}
                    onChange={e =>
                        setValue(old => ({
                            ...old,
                            recipientName: e.target.value,
                        }))
                    }
                />
                {isError && !value?.recipientName && (
                    <ErrorMessage>Recipient name is required</ErrorMessage>
                )}
            </div>

            <div {...part(FormPart.CONTROL)} className="mb-4">
                <QuestionLabel label="Recipient Email" />
                <EmailInput
                    {...part(FormPart.INPUT)}
                    onChange={value =>
                        setValue(old => ({ ...old, recipientEmail: value }))
                    }
                    value={value?.recipientEmail || ''}
                    error={
                        !value?.recipientName &&
                        !!value?.send_at &&
                        'Email address is required'
                    }
                />
            </div>

            <div {...part(FormPart.CONTROL)} className="mb-4">
                <QuestionLabel label="Message" />
                <textarea
                    {...part(FormPart.INPUT)}
                    className="w-full h-30"
                    value={value?.message || ''}
                    onChange={e =>
                        setValue(old => ({ ...old, message: e.target.value }))
                    }
                />
            </div>
            <div className="mb-4">
                <AmountOptions
                    onChange={value =>
                        setValue(old => ({ ...old, amount: value }))
                    }
                    value={value?.amount || ''}
                    settings={settings}
                />
                {isError && !value?.amount && (
                    <ErrorMessage>Please set an amount</ErrorMessage>
                )}
            </div>

            <SendOnInput
                value={value?.send_at}
                onChange={value => {
                    console.log('sendAt=', value);
                    setValue(old => ({ ...old, send_at: value }));
                }}
            />
        </div>
    );
};

export default GiftCertificateQuestion;
